import { md5 } from 'js-md5'
import {
  type AppliedReduction,
  type BasketResponseData,
  type CentAmount,
  getFirstAttributeValue,
  getAttributeValueTuples,
  type ListOfPackages,
  type PackageReference,
  type Price,
  type Product,
  type ProductCategory,
  type Value,
  type Variant,
  type WishlistResponseData,
} from '@scayle/storefront-nuxt'
import { isEqual } from './object'
import type { Order } from '~/types/order'
import { divideByHundred, formatPriceWithDecimals } from '~/utils/price'
import type {
  AdditionalInfo,
  AdditionalTrackingEvent,
  CustomerData,
  CustomerInfo,
  EcommerceData,
  EcommerceItem,
  EcommerceTrackingEvent,
  MultipleActionData,
  ProductActionData,
  ProductInfo,
  ProductListData,
  ProductViewData,
  TrackingCategory,
  TrackingEvent,
  TrackingPayload,
  ViewInfo,
} from '~/types/tracking'

/**
 * Checks if event is one of: AdditionalTrackingEvent = 'cart_summary' | 'wishlist' | 'search' | 'filter_flyout' | 'filter_slider'
 */
export const isAdditionalTrackingEvent = (
  event: TrackingEvent,
): event is AdditionalTrackingEvent => {
  return ['cart_summary', 'wishlist'].includes(event as AdditionalTrackingEvent)
}

/**
 * Checks if event is one of: 'add_to_wishlist' | 'remove_from_wishlist' | 'select_item' | 'view_item_list'
 */
export const isEcommerceTrackingEvent = (event: TrackingEvent): boolean => {
  return [
    'add_to_wishlist',
    'remove_from_wishlist',
    'select_item',
    'view_item_list',
    'view_item',
    'add_to_cart',
    'remove_from_cart',
    'begin_checkout',
    'purchase',
  ].includes(event)
}

export const isViewCartEvent = (
  event: TrackingEvent,
): event is EcommerceTrackingEvent => {
  return ['view_cart'].includes(event as EcommerceTrackingEvent)
}

/**
 * Checks if payload.items exists
 */
export const isProductImpressionsData = (
  data: TrackingPayload | MultipleActionData,
): data is MultipleActionData => {
  return !!(data as MultipleActionData).items
}

/**
 * Helpers
 */
const firstAttr = (
  // eslint-disable-next-line sonarjs/use-type-alias
  attrs: Product['attributes'] | Variant['attributes'] | undefined,
  code: string,
) => getFirstAttributeValue(attrs, code)

const labelOf = (
  attrs: Product['attributes'] | Variant['attributes'] | undefined,
  code: string,
) => firstAttr(attrs, code)?.label

const valueOf = (
  attrs: Product['attributes'] | Variant['attributes'] | undefined,
  code: string,
) => firstAttr(attrs, code)?.value

const toStr = (v: unknown) => (v == null ? '' : String(v))

const computeAvailabilityShare = (product: Product): number | undefined => {
  const variants = product.variants
  if (!Array.isArray(variants) || variants.length === 0) {
    return undefined
  }

  // If none of the variants has stock data, return undefined (mirror original behavior)
  const hasStockData = variants.some((v) => v?.stock !== undefined)
  if (!hasStockData) {
    return undefined
  }

  const total = variants.length
  const available = variants.filter((v) => (v?.stock?.quantity ?? 0) > 0).length
  if (total === 0) {
    return undefined
  }

  const ratio = available / total
  return Math.round(ratio * 100) / 100
}

const computePrices = (price?: {
  withTax: number
  withoutTax: number
  appliedReductions?: Array<{
    amount?: { relative?: number; absoluteWithTax?: number }
    category?: string
  }>
}) => {
  if (!price) {
    return {
      priceFormatted: 0.0,
      priceNet: 0,
      originalPrice: 0,
      reduction: undefined as
        | {
            typeWws: 'sale' | 'regular'
            percent?: number
            absolute?: number
            category?: string
          }
        | undefined,
    }
  }

  const applied = price.appliedReductions?.[0]
  const original = divideByHundred(getOriginalPrice(price))

  return {
    priceFormatted: formatPriceWithDecimals(price.withTax),
    priceNet: divideByHundred(price.withoutTax),
    originalPrice: original,
    reduction: applied
      ? {
          typeWws: 'sale' as const,
          percent: applied?.amount?.relative,
          absolute: applied?.amount?.absoluteWithTax
            ? divideByHundred(applied?.amount?.absoluteWithTax)
            : 0.0,
          category: applied?.category,
        }
      : { typeWws: 'regular' as const },
  }
}

const categoryFields = (product: Product) => {
  const deepest = getDeepestCategoryAttribute(product)
  const label = deepest?.label ?? ''
  const parts = label ? label.split('|') : []
  return {
    item_category: parts.length ? parts.join(' > ') : '',
    item_category2: parts.length ? parts[parts.length - 1] : '',
    item_category3: deepest?.value || '',
    item_category4: getItemCategory4FromProduct(product),
  }
}

const articleNoSearchFields = (product: Product) => {
  const raw = valueOf(product.attributes, 'articleNoSearch')
  const str = raw == null ? '' : String(raw)
  return {
    item_articlenumber: str,
    item_category5: str ? str.substring(0, 4) : '',
  }
}

const variantGate = (event?: TrackingEvent, explicitVariant?: Variant) => {
  const isListView = event === 'view_item_list'
  return {
    effectiveVariant: isListView ? undefined : explicitVariant, // keep original intent
    canUseVariant: !isListView && !!explicitVariant,
  }
}

const exclusivityField = (product: Product) =>
  valueOf(product.attributes, 'onlineExclusiveFilter') === 'yes'
    ? 'Online Exklusiv'
    : undefined

const snipesExclusivityField = (product: Product) =>
  valueOf(product.attributes, 'isExclusive') === 'yes'
    ? 'Snipes Exklusiv'
    : undefined

/**
 * Creates an EcommerceItem from product data (refactored)
 */
export const mapProductToEcommerceItem = (
  product: Product,
  variant?: Variant,
  additionalData?: Partial<EcommerceItem>,
  event?: TrackingEvent,
): EcommerceItem => {
  const priceSrc = variant ? variant.price : product.priceRange?.min
  const { priceFormatted, priceNet, originalPrice, reduction } =
    computePrices(priceSrc)

  const { effectiveVariant, canUseVariant } = variantGate(event, variant)

  const itemBrand = labelOf(product.attributes, 'brand') || ''
  const itemBrandId = toStr(firstAttr(product.attributes, 'brand')?.id)
  const itemManufacturerId = toStr(
    valueOf(product.attributes, 'manufacturerCode'),
  )
  const itemProductCode =
    labelOf(product.attributes, 'productCode') || product.referenceKey || ''

  const itemVariantLabel = labelOf(effectiveVariant?.attributes, 'size')
  const variantId = canUseVariant ? toStr(effectiveVariant?.id) : ''
  const itemSku = canUseVariant ? effectiveVariant?.referenceKey : undefined

  const availabilityShare = computeAvailabilityShare(product)
  const { item_category, item_category2, item_category3, item_category4 } =
    categoryFields(product)
  const { item_articlenumber, item_category5 } = articleNoSearchFields(product)

  const item_age = (
    labelOf(product.attributes, 'phasesOfLife') || ''
  ).toLowerCase()
  const item_gender = (
    labelOf(product.attributes, 'genders') || ''
  ).toLowerCase()

  const item_hr =
    (valueOf(product.attributes, 'isHotRelease') || '')
      .toString()
      .toLowerCase() === 'yes'

  const item_exclusivity = exclusivityField(product)
  const item_concept_exclusivity = snipesExclusivityField(product)
  const item_hy_code =
    valueOf(product.attributes, 'affiliateRevenueModel') || ''

  return {
    item_id: product.id.toString(),
    item_name: labelOf(product.attributes, 'name')!, // original code asserted non-null
    item_brand: itemBrand,

    item_category,
    item_category2,
    item_category3,
    item_category4,
    item_category5,

    price: priceFormatted,
    quantity: 1,

    // Variant-specific fields only when allowed
    item_variant:
      canUseVariant && variantId && itemVariantLabel
        ? itemVariantLabel
        : undefined,
    item_variant_id:
      canUseVariant && variantId && itemVariantLabel ? variantId : undefined,
    item_sku: itemSku,

    item_brand_code: itemBrandId,
    item_manufacturer_id: itemManufacturerId,
    item_articlenumber,
    item_age,
    item_gender,
    item_product_code: itemProductCode,

    item_price_original: originalPrice,
    item_price_net: priceNet,
    item_price_type_wws: reduction?.typeWws ?? 'regular',
    item_discount_percentage: reduction?.percent,
    item_discount: reduction?.absolute,
    item_discount_type: reduction?.category,

    item_exclusivity,
    item_concept_exclusivity,
    item_hr,
    item_availability_share: availabilityShare,
    item_hy_code,

    item_list_name: '',
    item_list_id: '',

    ...additionalData,
  }
}

/**
 * Calculates the total value (original price minus sale discount) from ecommerce items
 */
export const calculateEcommerceValueWithDiscount = (
  items: EcommerceItem[],
): number => {
  const totalValue = items.reduce((total, item) => {
    const priceAfterSaleDiscount = item.price || 0
    return total + priceAfterSaleDiscount * item.quantity
  }, 0)

  return Number(totalValue.toFixed(2))
}

/**
 * Calculates the total value (original price minus sale discount) from ecommerce items
 */
export const calculateEcommerceValue = (items: EcommerceItem[]): number => {
  const totalValue = items.reduce((total, item) => {
    const priceAfterSaleDiscount = item.item_price_original || 0
    return total + priceAfterSaleDiscount * item.quantity
  }, 0)

  return Number(totalValue.toFixed(2))
}

export const mapProductToTrackingPayload = (
  product: Product & { index?: number },
  variant?: Variant,
  event?: TrackingEvent,
): ProductInfo => {
  const price = variant ? variant.price : product.priceRange?.min

  const itemBrand =
    getFirstAttributeValue(product.attributes, 'brand')?.label || ''
  const itemBrandId =
    getFirstAttributeValue(product.attributes, 'brand')?.id?.toString() || ''
  const itemManufacturerId =
    getFirstAttributeValue(
      product.attributes,
      'manufacturerCode',
    )?.value?.toString() || ''
  const itemProductCode =
    getFirstAttributeValue(product.attributes, 'productCode')?.label || ''

  // Use the passed variant only when explicitly provided, no fallback to first variant
  const effectiveVariant = event === 'view_item_list' ? undefined : variant
  const variantId =
    event === 'view_item_list' ? '' : effectiveVariant?.id?.toString() || ''
  const itemVariant = getFirstAttributeValue(
    effectiveVariant?.attributes,
    'size',
  )?.label

  // Calculate availability share: percentage of available sizes vs total sizes
  const availabilityShare = (() => {
    const variants = product.variants
    if (!variants || !Array.isArray(variants) || variants.length === 0) {
      return undefined
    }

    // Check if variants have stock data loaded - if not, return undefined
    const hasStockData = variants.some((v: any) => v?.stock !== undefined)
    if (!hasStockData) {
      return undefined
    }

    const totalVariants = variants.length
    const availableVariants = variants.filter(
      (v: any) => v?.stock?.quantity && v.stock.quantity > 0,
    ).length

    if (totalVariants > 0) {
      const ratio = availableVariants / totalVariants
      return Math.round(ratio * 100) / 100 // Round to 2 decimal places
    }
    return undefined
  })()

  // Check for online exclusivity
  const isOnlineExclusive =
    getFirstAttributeValue(product.attributes, 'onlineExclusiveFilter')
      ?.value === 'yes'
  const exclusivity = isOnlineExclusive ? 'Online Exklusiv' : undefined

  // Check for Snipes exclusivity
  const isExclusive =
    getFirstAttributeValue(product.attributes, 'isExclusive')?.value === 'yes'
  const snipesExclusivity = isExclusive ? 'Snipes Exklusiv' : undefined

  const deepestCategory = getDeepestCategoryAttribute(product)

  return {
    item_id: product.id.toString(),
    item_name: getFirstAttributeValue(product.attributes, 'name')!.label,
    ...(price
      ? (() => {
          const originalPriceValue = divideByHundred(getOriginalPrice(price))
          const appliedReduction = price.appliedReductions?.[0]
          return {
            item_price_net: divideByHundred(price.withoutTax),
            price: formatPriceWithDecimals(price.withTax),
            item_price_original: originalPriceValue,
            item_price_type_wws: appliedReduction ? 'sale' : 'regular',
            item_discount_percentage: appliedReduction
              ? appliedReduction?.amount?.relative
              : undefined,
            item_discount: appliedReduction
              ? divideByHundred(appliedReduction?.amount?.absoluteWithTax)
              : undefined,
            item_discount_type: appliedReduction
              ? appliedReduction?.category
              : undefined,
          }
        })()
      : {
          item_price_net: undefined,
          price: 0.0,
          item_price_original: undefined,
          item_price_type_wws: 'regular',
          item_discount_percentage: undefined,
          item_discount: undefined,
          item_discount_type: undefined,
        }),
    item_brand: itemBrand,
    item_brand_code: itemBrandId,
    item_manufacturer_id: itemManufacturerId,
    item_product_code: itemProductCode,
    ...(event !== 'view_item_list' &&
      variantId && { item_variant_id: variantId }),
    ...(variantId &&
      itemVariant &&
      event !== 'view_item_list' && { item_variant: itemVariant }),
    item_category: deepestCategory?.label.split('|').join(' > ') || '',
    item_category2: deepestCategory?.label.split('|').pop() || '',
    item_category3: deepestCategory?.value || '',
    item_category4: getItemCategory4FromProduct(product),
    item_concept_exclusivity: snipesExclusivity,
    item_availability_share: availabilityShare,
    item_exclusivity: exclusivity,
    item_hy_code:
      getFirstAttributeValue(product.attributes, 'affiliateRevenueModel')
        ?.value || '',
    item_category5: (() => {
      const articleNoSearch = getFirstAttributeValue(
        product.attributes,
        'articleNoSearch',
      )?.value
      if (articleNoSearch) {
        return articleNoSearch.toString().substring(0, 4)
      }
      return ''
    })(),
    item_articlenumber: (() => {
      const articleNoSearch = getFirstAttributeValue(
        product.attributes,
        'articleNoSearch',
      )?.value
      if (articleNoSearch) {
        return articleNoSearch.toString()
      }
      return ''
    })(),
    item_age: (() => {
      const phasesOfLife = getFirstAttributeValue(
        product.attributes,
        'phasesOfLife',
      )
      if (phasesOfLife?.label) {
        return phasesOfLife.label.toLowerCase()
      }
      return ''
    })(),
    item_gender: (() => {
      const genders = getFirstAttributeValue(product.attributes, 'genders')
      if (genders?.label) {
        return genders.label.toLowerCase()
      }
      return ''
    })(),
    ...(effectiveVariant?.id &&
      effectiveVariant?.referenceKey &&
      event !== 'view_item_list' && {
        item_sku: effectiveVariant.referenceKey,
      }),
    item_hr: (() => {
      const isHotRelease = getFirstAttributeValue(
        product.attributes,
        'isHotRelease',
      )
      if (isHotRelease?.value) {
        return isHotRelease.value.toLowerCase() === 'yes'
      }
      return false
    })(),
  }
}

const mapAdditionalInfo = (
  data: ProductActionData | ProductListData | ProductViewData,
  event?: TrackingEvent,
): AdditionalInfo | ViewInfo => {
  const {
    product,
    list,
    quantity = 1,
  } = data as ProductViewData & ProductActionData

  const deepestCategory = getDeepestCategoryAttribute(product)

  return {
    item_category: deepestCategory?.label.split('|').join(' > ') || '',
    item_category2: deepestCategory?.label.split('|').pop() || '',
    item_category3: deepestCategory?.value || '',
    item_category4: getItemCategory4FromProduct(product),
    // Only include item_variant_id if explicitly provided in data.variant
    ...('variant' in data &&
      data.variant?.id &&
      event !== 'view_item_list' && {
        item_variant_id: data.variant.id.toString(),
      }),
    item_list_name: list?.name || '',
    item_list_id: list?.id || '',
    quantity: quantity > -1 ? String(quantity) : '0',
    index: product?.index || 0,
    // item_variant_id already handled above
  } as AdditionalInfo | ViewInfo
}

/**
 * Checks if a product has any of the category type attributes and returns the attribute name
 * @param product - Product to check for category type attributes
 * @returns The attribute name ('accessoryType', 'shoeType', 'apparelType') if found, otherwise undefined
 */
const getItemCategory4FromProduct = (product: Product): string | undefined => {
  const typeAttributes = ['accessoryType', 'shoeType', 'apparelType']

  for (const attributeType of typeAttributes) {
    const attributeValue = getFirstAttributeValue(
      product.attributes,
      attributeType,
    )
    if (attributeValue) {
      return attributeType
    }
  }

  return undefined
}

export { mapAdditionalInfo, getItemCategory4FromProduct }

const getTotalPriceInfo = (
  items: {
    quantity: number
    price?: number
    priceWithTax?: number
  }[],
) => {
  const total = {
    total_with_tax: 0.0,
    total_without_tax: 0.0,
  }

  items.forEach((item) => {
    total.total_with_tax += (item.priceWithTax ?? 0) * item.quantity
    total.total_without_tax += (item.price ?? 0) * item.quantity
  })

  const keys = Object.keys(total) as (keyof typeof total)[]

  keys.forEach(
    (key) => (total[key] = Number((total[key] as number).toFixed(2))),
  )

  return total
}

export const mapCustomerInfoToTrackingPayload = (
  {
    method,
    eh,
    status,
    login_method: loginMethod,
    customer_id: customerId,
    customer_type: customerType = 'new',
    pagePayload,
  }: CustomerData,
  clean: boolean = false,
): CustomerInfo => {
  const mappedPayload: CustomerInfo = {
    ...(loginMethod ? { login_method: loginMethod } : { method }),
    eh: eh || '',
    customer_type: customerType,
    ...(pagePayload ?? {}),
    status,
  }

  if (clean) {
    if (mappedPayload.customer_type) {
      // @ts-expect-error - customer_type is optional in CustomerInfo
      delete mappedPayload.customer_type
    }
  }

  if (customerId) {
    mappedPayload.customer_id = customerId
  }

  return mappedPayload
}

/**
 * Maps tracking events to their corresponding action strings
 */
const getActionForEvent = (
  event: TrackingEvent,
  payload?: TrackingPayload,
): string | undefined => {
  const actionMap: Record<string, string> = {
    select_item: 'select item from list',
    view_item: 'view item',
    view_cart: 'view cart',
    remove_from_cart: 'remove item from cart',
    add_to_wishlist: 'add item to wishlist',
    remove_from_wishlist: 'remove item from wishlist',
  }

  // Special handling for add_to_cart event
  if (event === 'add_to_cart' && payload) {
    // Check if payload has actionType field to distinguish between add and change
    if ('actionType' in payload && payload.actionType === 'change_quantity') {
      return 'change quantity'
    }
    return 'add item to cart'
  }

  return actionMap[event]
}

/**
 * Creates ecommerce data for view cart events
 */
const createViewCartEcommerceData = (
  payload: MultipleActionData,
): { ecommerce: EcommerceData; category: string } => {
  const { currencyCode: currency, valueWithoutTax: value, items } = payload

  const ecommerceItems: EcommerceItem[] = items.map((payload) => {
    const price = payload.product.priceRange?.min
    const additionalInfo = mapAdditionalInfo(payload, event)

    return mapProductToEcommerceItem(
      payload.product,
      undefined,
      {
        ...additionalInfo,
        quantity: payload.quantity || 1,
        tax: divideByHundred(price?.tax?.vat?.amount || 0),
      },
      undefined,
    )
  })

  const ecommerceData: EcommerceData = {
    currency: currency || 'EUR',
    value: value || calculateEcommerceValue(ecommerceItems),
    items: ecommerceItems,
  }

  return { ecommerce: ecommerceData, category: 'ecommerce' }
}

/**
 * Creates ecommerce data for product impressions
 */
const createProductImpressionsEcommerceData = (
  payload: MultipleActionData,
  event?: TrackingEvent,
): { ecommerce: EcommerceData; category: string } => {
  const { currencyCode: currency } = payload

  const ecommerceItems: EcommerceItem[] = payload.items.map((payload) => {
    const price = payload.product.priceRange?.min
    const additionalInfo = mapAdditionalInfo(payload, event)

    // Don't include tax property for view_item_list events
    const shouldIncludeTax = event !== 'view_item_list'

    return mapProductToEcommerceItem(
      payload.product,
      undefined,
      {
        ...additionalInfo,
        quantity: payload.quantity || 1,
        ...(shouldIncludeTax && {
          tax: divideByHundred(price?.tax?.vat?.amount || 0),
        }),
      },
      event,
    )
  })

  const ecommerceData: EcommerceData = {
    currency: currency || 'EUR',
    items: ecommerceItems,
    // Exclude value property for view_item_list events
    ...(event !== 'view_item_list' && {
      value: calculateEcommerceValue(ecommerceItems),
    }),
  }

  return { ecommerce: ecommerceData, category: 'ecommerce' }
}

/**
 * Creates ecommerce data for single product events
 */
const createSingleProductEcommerceData = (
  payload: ProductActionData,
): { ecommerce: EcommerceData; category: string } => {
  const currency = payload.currencyCode || 'EUR'

  const additionalInfo = mapAdditionalInfo(payload, undefined)
  const ecommerceItem = mapProductToEcommerceItem(
    payload.product!,
    'variant' in payload ? payload.variant : undefined,
    {
      ...additionalInfo,
      quantity: 'quantity' in payload ? payload.quantity || 1 : 1,
    },
    undefined,
  )

  const ecommerceData: EcommerceData = {
    currency,
    value: calculateEcommerceValueWithDiscount([ecommerceItem]),
    items: [ecommerceItem],
  }

  return { ecommerce: ecommerceData, category: 'ecommerce' }
}

/**
 * Creates ecommerce data for multiple products events
 */
const createMultipleProductsEcommerceData = (
  payload: ProductActionData,
): { ecommerce: EcommerceData; category: string } => {
  const currency = payload.currencyCode || 'EUR'

  const ecommerceItems: EcommerceItem[] = payload.products!.map((product) => {
    const additionalInfo = mapAdditionalInfo({ product })

    return mapProductToEcommerceItem(
      product,
      undefined,
      {
        ...additionalInfo,
        quantity: 1,
      },
      undefined,
    )
  })

  const ecommerceData: EcommerceData = {
    currency,
    value: calculateEcommerceValue(ecommerceItems),
    items: ecommerceItems,
  }

  return { ecommerce: ecommerceData, category: 'ecommerce' }
}

/**
 * Checks if event is a wishlist event from product carousel
 */
const isWishlistEventFromCarousel = (
  event: TrackingEvent,
  payload: TrackingPayload,
): boolean => {
  const isWishlistEvent =
    event === 'add_to_wishlist' || event === 'remove_from_wishlist'

  if (!isWishlistEvent) {
    return false
  }

  const hasPagePayload = 'pagePayload' in payload && payload.pagePayload

  return (
    // Primary check: explicit carousel metadata
    ('list' in payload &&
      payload.list &&
      (payload.list.id === 'PS' ||
        payload.list.name?.startsWith('ProductSlider') === true)) ||
    // Fallback check: detect carousel context from page state
    // If we're on homepage and no other specific list metadata is present,
    // assume it's from a product carousel (common homepage pattern)
    (hasPagePayload &&
      payload.pagePayload?.page_type === 'homepage' &&
      (!('list' in payload) ||
        !payload.list?.name ||
        payload.list?.name === '')) ||
    false
  )
}

/**
 * Checks if event is a wishlist event from category page
 */
const isWishlistEventFromCategory = (
  event: TrackingEvent,
  pageType: string,
): boolean => {
  const isWishlistEvent =
    event === 'add_to_wishlist' || event === 'remove_from_wishlist'
  return isWishlistEvent && pageType === 'category_page'
}

/**
 * Gets the appropriate label for tracking events
 */
const getEventLabel = (
  event: TrackingEvent,
  payload: TrackingPayload,
): string | undefined => {
  if (event === 'view_item') {
    return 'product detail page'
  }
  if (event === 'view_cart') {
    return 'cart page'
  }

  // Handle ecommerce events
  const isEcommerceEvent = [
    'add_to_cart',
    'remove_from_cart',
    'add_to_wishlist',
    'remove_from_wishlist',
  ].includes(event)

  if (!isEcommerceEvent) {
    return undefined
  }

  // Check for product carousel wishlist events first
  if (isWishlistEventFromCarousel(event, payload)) {
    return 'product carousel'
  }

  // Check for basket flyout remove events
  if (
    event === 'remove_from_cart' &&
    'location' in payload &&
    (payload as any).location === 'flyout cart'
  ) {
    return 'flyout cart'
  }

  // Use page_type from pagePayload for other ecommerce events
  if (
    !('pagePayload' in payload) ||
    !payload.pagePayload ||
    !('page_type' in payload.pagePayload)
  ) {
    return undefined
  }

  const pageType = payload.pagePayload.page_type
  if (!pageType) {
    return undefined
  }

  // Special case for category page wishlist events
  if (isWishlistEventFromCategory(event, pageType)) {
    return 'category'
  }

  // Default: replace underscores with spaces
  return pageType.replace(/_/g, ' ')
}

/**
 * Handles additional tracking events with product impressions
 */
const handleAdditionalTrackingEvent = (
  payload: MultipleActionData,
): Record<string, any> => {
  const items = payload.items.map((payloadItem) => ({
    ...mapProductToTrackingPayload(payloadItem.product, undefined, undefined),
    ...mapAdditionalInfo(payloadItem, undefined),
    currency: 'currencyCode' in payload ? payload.currencyCode : undefined,
  }))

  const totalPrice = getTotalPriceInfo(
    items.map((item) => ({
      price: item.item_price_net,
      quantity: item.quantity ? parseInt(item.quantity!) : 1,
      priceWithTax: item.price, // Use gross price after discounts
    })),
  )

  // Preserve additional properties like category, action, label
  const additionalProps: Record<string, any> = {}
  if ('category' in payload && payload.category) {
    additionalProps.category = payload.category
  }
  if ('action' in payload && payload.action) {
    additionalProps.action = payload.action
  }
  if ('label' in payload && payload.label) {
    additionalProps.label = payload.label
  }
  if ('currencyCode' in payload && payload.currencyCode) {
    additionalProps.currencyCode = payload.currencyCode
  }

  return { items, ...totalPrice, ...additionalProps }
}

/**
 * Handles ecommerce tracking events based on payload type
 */
const handleEcommerceEvent = (
  event: TrackingEvent,
  payload: TrackingPayload,
  action?: string,
): Record<string, any> => {
  if (isViewCartEvent(event)) {
    const { pagePayload } = payload as MultipleActionData
    const ecommerceResult = createViewCartEcommerceData(
      payload as MultipleActionData,
    )
    return {
      ...(pagePayload || {}),
      ...ecommerceResult,
      ...(action && { action }),
    }
  }

  if (isProductImpressionsData(payload)) {
    const { pagePayload } = payload
    const ecommerceResult = createProductImpressionsEcommerceData(
      payload,
      event,
    )
    return {
      ...(pagePayload || {}),
      ...ecommerceResult,
      ...(action && { action }),
    }
  }

  if ('product' in payload && 'currencyCode' in payload && payload.product) {
    const { pagePayload } = payload
    const ecommerceResult = createSingleProductEcommerceData(
      payload as ProductActionData,
    )
    return {
      ...(pagePayload || {}),
      ...ecommerceResult,
      ...(action && { action }),
    }
  }

  if ('products' in payload && 'currencyCode' in payload && payload.products) {
    const { pagePayload } = payload
    const ecommerceResult = createMultipleProductsEcommerceData(
      payload as ProductActionData,
    )
    return {
      ...(pagePayload || {}),
      ...ecommerceResult,
      ...(action && { action }),
    }
  }

  return {
    ...payload,
    ...(action && { action }),
  }
}

export const mapTrackingDataForEvent = (
  event: TrackingEvent,
  payload: TrackingPayload,
) => {
  const action = getActionForEvent(event, payload)
  const label = getEventLabel(event, payload)
  const finalLabel = label ?? (payload as any).label

  let data: Record<string, any> = {}

  if (isAdditionalTrackingEvent(event) && isProductImpressionsData(payload)) {
    data = handleAdditionalTrackingEvent(payload)
  } else if (isEcommerceTrackingEvent(event)) {
    data = handleEcommerceEvent(event, payload, action)
  } else {
    data = {
      ...payload,
      ...(action && { action }),
    }
  }

  return {
    event,
    ...data,
    ...(finalLabel && { label: finalLabel }),
    ...(isEcommerceTrackingEvent(event) && { _clear: true }),
  }
}

/**
 * @param categories
 * @returns most specific category for a product
 */
function getDeepestCategory(
  categories: ProductCategory[][] = [],
): ProductCategory | null {
  if (!categories.length) {
    return null
  }
  let depth = { index: 0, value: 0 }
  categories.forEach((category, index) => {
    if (category.length > depth.value) {
      depth = { index, value: category.length }
    }
    if (category.length === depth.value) {
      depth.index = index
    }
  })

  return categories[depth.index][categories[depth.index].length - 1]
}

/**
 * @param product
 * @returns most specific category for a product
 */
export function getDeepestCategoryAttribute(product: Product): Value | null {
  if (!product || !product?.attributes?.category) {
    return null
  }

  const deepestCategories = getAttributeValueTuples(
    product?.attributes,
    'category',
  )
  return [...deepestCategories]?.pop() ?? null
}

export const getCategoryPathForTracking = (
  categories: ProductCategory[][] = [],
): string => {
  const deepestCategoryPath = getDeepestCategory(categories)
  if (!deepestCategoryPath || !categories.length) {
    return ''
  }

  // Find the deepest category array (longest path)
  let deepestPath: ProductCategory[] = []
  let maxDepth = 0

  categories.forEach((categoryPath) => {
    if (categoryPath.length > maxDepth) {
      maxDepth = categoryPath.length
      deepestPath = categoryPath
    }
  })

  // Join all category names with " > "
  return deepestPath.map((category) => category.categoryName).join(' > ')
}

export const getLastCategoryFromPath = (categoryPath: string): string => {
  if (!categoryPath) {
    return ''
  }
  const categories = categoryPath.split(' > ')
  return categories[categories.length - 1] || ''
}

export const getDeepestCategoryForTracking = (
  categories: ProductCategory[][] = [],
): TrackingCategory => {
  const { categoryName, categoryId } = getDeepestCategory(categories) || {
    categoryName: '',
    categoryId: '',
  }
  return {
    name: categoryName,
    id: (categoryId || '').toString(),
  }
}

export const didBasketDataChange = (
  oldData: BasketResponseData | null | undefined,
  newData: BasketResponseData | null | undefined,
) => {
  return !isEqual(
    {
      items: oldData?.items.map((item) => ({
        productId: item.product?.id,
        variantId: item.variant?.id,
        price: item.product?.priceRange,
        quantity: item.quantity,
        soldOut: item.product?.isSoldOut,
      })),
      key: oldData?.key,
    },
    {
      items: newData?.items.map((item) => ({
        productId: item.product?.id,
        variantId: item.variant?.id,
        price: item.product?.priceRange,
        quantity: item.quantity,
        soldOut: item.product?.isSoldOut,
      })),
      key: newData?.key,
    },
  )
}

export const didWishlistDataChange = (
  oldData: WishlistResponseData | null | undefined,
  newData: WishlistResponseData | null | undefined,
) => {
  return !isEqual(
    {
      items: oldData?.items.map((item) => ({
        productId: item.product?.id,
        variantId: item.variant?.id,
        soldOut: item.product?.isSoldOut,
      })),
      key: oldData?.key,
    },
    {
      items: newData?.items.map((item) => ({
        productId: item.product?.id,
        variantId: item.variant?.id,
        soldOut: item.product?.isSoldOut,
      })),
      key: newData?.key,
    },
  )
}

export const formatPriceWithCurrency = (
  value: number,
  locale: string,
  currency: string,
): string => {
  const currencyFractionDigits = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).resolvedOptions().maximumFractionDigits

  return divideByHundred(value).toLocaleString(locale, {
    minimumFractionDigits: currencyFractionDigits,
  })
}

export const sumReductions = (reductions: AppliedReduction[]) => {
  if (!reductions) {
    return 0
  }
  return reductions.reduce((sum: number, reduction: AppliedReduction) => {
    return sum + reduction.amount.absoluteWithTax
  }, 0)
}

export const sumReductionsByCategory = (
  reductions?: AppliedReduction[],
  category?: AppliedReduction['category'],
) => {
  if (!reductions) {
    return 0
  }
  return sumReductions(
    reductions.filter((reduction) => reduction.category === category),
  )
}

export const sumReductionsFromAllOrderItemsPerCategory = (
  orderItems: Order['items'],
  category: AppliedReduction['category'],
) => {
  if (!orderItems) {
    return 0
  }
  return orderItems.reduce(
    (sum: number, orderItem: NonNullable<Order['items']>[number]) => {
      return (
        sum +
        sumReductionsByCategory(
          orderItem?.price?.appliedReductions as AppliedReduction[],
          category,
        )
      )
    },
    0,
  )
}

export const getFirstCarrierKey = (orderData: Order) => {
  return orderData.packages?.[0]?.carrierKey
}

export const getCarrier = (
  packages: Order['packages'],
  packageId: PackageReference,
) => {
  return packages?.find((p) => p.id === packageId) as ListOfPackages[0]
}

export const getGiftcardAmount = ({
  amount,
  currency,
  locale,
}: {
  amount: number
  currency: string
  locale: string
}) => {
  const numberParts = new Intl.NumberFormat(locale.replace('_', '-'), {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).formatToParts(amount)
  const currencySymbol = numberParts.find(({ type }) => type === 'currency')
    ?.value
  const number = numberParts
    .filter(({ type }) => !['currency', 'literal'].includes(type))
    .map(({ value }) => value)
    .join('')
  return `${number}${currencySymbol}`
}

/**
 * Orders can only have vouchers(same as coupons) with a particular type, like 'relative' or 'absolute'. Dependending on this type the value is calculated differently.
 *
 * voucherType is optional and is derived from orderData if not provided
 * @param orderData
 * @param [voucherType]
 * @returns
 */
export const getCouponReductionWithTax = ({
  orderData,
  voucherType,
}: {
  orderData: Order
  voucherType?: 'absolute' | 'relative'
}) => {
  const orderVoucherType = voucherType ?? orderData.vouchers?.[0]?.type
  if (
    !orderData.vouchers?.[0] ||
    orderData.vouchers?.length === 0 ||
    orderVoucherType === undefined
  ) {
    return 0
  }
  if (orderVoucherType === 'absolute') {
    return divideByHundred(orderData.vouchers[0].value)
  }
  if (orderVoucherType === 'relative') {
    return divideByHundred(
      sumReductionsFromAllOrderItemsPerCategory(orderData.items, 'voucher'),
    )
  }
}

export const getEmailHash = async (email: string | undefined) => {
  if (!email) {
    return undefined
  }
  const hashBuffer = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(email.replace(/ /g, '')?.toLowerCase()),
  )
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

export const getEmailHashMD5 = async (email: string | undefined) => {
  if (!email) {
    return undefined
  }
  return md5(email.replace(/ /g, '').toLowerCase())
}

const getOriginalPrice = (price: Price): CentAmount =>
  price.appliedReductions?.reduce(
    (previousPrice, appliedReduction) =>
      (previousPrice + appliedReduction.amount.absoluteWithTax) as CentAmount,
    price.withTax,
  )
